
import { useState } from 'react';
import { ref, set } from 'firebase/database';
import { database } from '../firebase/firebase';
import { useEventUsersCache } from './useEventUsersCache';
import { BankInfo } from '../types';

// Define a specific interface for event user profile
export interface EventUserProfile {
    displayName: string;
    email?: string;
    createdAt: number;
    lastAccess: number;
    bankInfo?: BankInfo;
    isAnonymous?: boolean;
    userId?: string;
    anonymousId?: string;
}

export const useEventUser = (eventId?: string | null, userId?: string | null) => {
    const [updateLoading, setUpdateLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { loading, getEventUser, error: cacheError } = useEventUsersCache(eventId || undefined);

    // Get user profile from cache with correct typing
    const userProfile: EventUserProfile | null = eventId && userId
        ? getEventUser(eventId, userId)
        : null;

    const updateEventUserDisplayName = async (eventId: string, userId: string, displayName: string) => {
        setUpdateLoading(true);
        setError(null);

        try {
            const displayNameRef = ref(database, `eventUsers/${eventId}/${userId}/displayName`);
            await set(displayNameRef, displayName);
        } catch (error) {
            console.error('Error updating event user display name:', error);
            setError('Failed to update display name');
            throw error;
        } finally {
            setUpdateLoading(false);
        }
    };

    const updateEventUserBankInfo = async (eventId: string, userId: string, bankInfo: BankInfo) => {
        setUpdateLoading(true);
        setError(null);

        try {
            const bankInfoRef = ref(database, `eventUsers/${eventId}/${userId}/bankInfo`);
            await set(bankInfoRef, bankInfo);
        } catch (error) {
            console.error('Error updating event user bank info:', error);
            setError('Failed to update bank info');
            throw error;
        } finally {
            setUpdateLoading(false);
        }
    };


    return {
        loading: loading || updateLoading,
        userProfile,
        error: error || cacheError,
        updateEventUserDisplayName,
        updateEventUserBankInfo
    };
};